import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Link, Section, Spinner, Table } from '@factorialco/deprecated-design-system'
import { SettingPageEmployeesSettingFragment } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import SpecialHourWizard from 'modules/attendance/components/CustomTimeRanges/Content/Table/Modals/SpecialHourWizard'
import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import { useCustomTimeRange } from 'modules/attendance/hooks/useCustomTimeRange'
import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import { translateDayInSingular } from 'modules/attendance/lib/translateDays'
import CustomTimeRangeUtils from 'modules/attendance/lib/utils/CustomTimeRangeUtils'

import TableBody from './Body'
import HeaderCell from './Body/HeaderCell'
import Navigation from './Navigation'
import NewFormulaHint from './NewFormulaHint'
import { useEnableSpecialHoursRegularHoursCompensation } from './hooks/useEnableSpecialHoursRegularHoursCompensation'
import * as styles from './index.vanilla.css'

const SpecialWorkHours = ({ hasSpecialHoursBrazil }: { hasSpecialHoursBrazil: boolean }) => {
  const { loading, attendanceSetting, specialHoursRegularHoursCompensation, customTimeRanges } =
    useAttendanceSettingWorkCategories()
  const hasTimeRanges = CustomTimeRangeUtils.hasTimeRanges(customTimeRanges)
  const { dayEnums } = useCustomTimeRange()
  const [isAddingNewBlock, setIsAddingNewBlock] = React.useState(false)

  const [isCalloutDismissed, setIsCalloutDismissed] = React.useState(false)
  const isCalloutVisible =
    !hasSpecialHoursBrazil &&
    !specialHoursRegularHoursCompensation &&
    hasTimeRanges &&
    !isCalloutDismissed

  const [openSettingsVersionModal, setOpenSettingsVersionModal] = React.useState(false)
  const [openHint, setOpenHint] = React.useState(false)

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const onClick = () => {
    track(trackingEvents.timeTracking.newSpecialHourPressed)
    setIsAddingNewBlock(true)
  }

  const { enable: enableSpecialHoursRegularHoursCompensation } =
    useEnableSpecialHoursRegularHoursCompensation()

  const overwriteCurrentVersion = async () => {
    await enableSpecialHoursRegularHoursCompensation({ employeesSettingId: attendanceSetting.id })

    setOpenSettingsVersionModal(false)
  }

  const handleNewVersion = async (newSettingVersion: SettingPageEmployeesSettingFragment) => {
    await enableSpecialHoursRegularHoursCompensation({ employeesSettingId: newSettingVersion.id })

    setOpenSettingsVersionModal(false)

    const routeParams = {
      id: 'time_tracking',
      settingId: newSettingVersion.id,
      tabId: 'work_time_categories',
    }
    navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  if (loading)
    return (
      <Box justifyContent="center" alignItems="center" height="full">
        <Spinner />
      </Box>
    )

  const tableHeaders = dayEnums.map((day) => translateDayInSingular(day))

  return (
    <Section.FullWidth
      illustration="night"
      title={i18n.t('time_tracking.settings.work_time_categories.special_work_hours.section.title')}
      description={i18n.t(
        'time_tracking.settings.work_time_categories.special_work_hours.section.description'
      )}
      action={{
        onClick,
        label: i18n.t(
          'time_tracking.settings.work_time_categories.special_work_hours.section.action'
        ),
      }}
    >
      <Box gap="s24">
        {isCalloutVisible && (
          <Callout
            type="info"
            description={i18n.t(
              'time_tracking.settings.work_time_categories.special_work_hours.callout.description'
            )}
            title={i18n.t(
              'time_tracking.settings.work_time_categories.special_work_hours.callout.title'
            )}
            onRemove={() => setIsCalloutDismissed(true)}
            action={
              <>
                <Link.Touchable type="solid" onClick={() => setOpenHint(true)} expand>
                  {i18n.t(
                    'time_tracking.settings.work_time_categories.special_work_hours.callout.learn_more'
                  )}
                </Link.Touchable>
                <Button onClick={() => setOpenSettingsVersionModal(true)} type="secondary">
                  {i18n.t(
                    'time_tracking.settings.work_time_categories.special_work_hours.callout.button_action'
                  )}
                </Button>
              </>
            }
          />
        )}
        <Box gap="s24">
          <Box gap="s24" className={styles.gradient}>
            <Table density="compact" overflowX="auto" autoWidth>
              <Table.Head>
                <Table.Row>
                  {tableHeaders.map((title) => (
                    <HeaderCell key={title} text={title} />
                  ))}
                </Table.Row>
              </Table.Head>
              <TableBody />
            </Table>
          </Box>
          <Navigation />
        </Box>
      </Box>
      {isAddingNewBlock && <SpecialHourWizard onClose={() => setIsAddingNewBlock(false)} />}
      {openSettingsVersionModal && (
        <ManageSettingVersionModal
          onCreate={handleNewVersion}
          onUpdate={overwriteCurrentVersion}
          onClose={() => setOpenSettingsVersionModal(false)}
          running={false}
          attendanceSettingId={attendanceSetting.id}
          attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
        />
      )}
      {openHint && (
        <NewFormulaHint
          setOpenHint={setOpenHint}
          setOpenSettingsVersionModal={setOpenSettingsVersionModal}
        />
      )}
    </Section.FullWidth>
  )
}

export default SpecialWorkHours
